import React from 'react';
import {
    Button,
    Tooltip,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { AgentCapabilities } from '../../../domain/agents/models/AgentCapabilities';
import { ArrowDownload20Filled } from '@fluentui/react-icons';
import { ReactComponent as EditComponent } from '../../../assets/Edit.svg';
import { ReactComponent as EditDisabled } from '../../../assets/EditDisabled.svg';
import { Breakpoints, customColors } from '../../../styles';
import DeleteChatDialog from './dialogs/DeleteChatDialog';
import { useAppSelector } from '../../../redux/hooks';

const useStyles = makeStyles({
    root: {
        display: 'contents',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    rootExpanded: {
        display: 'contents',
    },
    actionButton: {
        color: customColors.white,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        minWidth: 'unset',
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
        ':hover': {
            backgroundColor: 'transparent',
            color: customColors.headerLightGray,
        },
        ':active': {
            backgroundColor: 'transparent',
            color: customColors.headerLightGray,
        },
    },
});

interface ChatListItemActionsProps {
    capabilities: AgentCapabilities;
    chatId: string;
    chatName: string;
    // isTitleGenerated?: boolean;
    forceExpanded?: boolean;
    onEditTitleClick: () => void;
    onDownloadChat: (chatId: string) => Promise<void>;
    onDeleteChat: (chatId: string) => Promise<void>;
}

const ChatListItemActions: React.FC<ChatListItemActionsProps> = ({
    capabilities,
    chatId,
    chatName,
    // isTitleGenerated,
    forceExpanded,
    onEditTitleClick,
    onDownloadChat,
    onDeleteChat,
}) => {
    const styles = useStyles();
    const sessionId = useAppSelector(state => state.chatUi.selectedSessionId);
    const isTitleGenerating = useAppSelector(state =>
        sessionId ? state.chatUi.bySessionId[sessionId]?.isTitleGenerating ?? false : false
    );
    return (
        <div className={forceExpanded ? styles.rootExpanded : styles.root}>
            <Tooltip content={'Edit chat name'} relationship="label">
                <Button
                    className={styles.actionButton}
                    size="small"
                    appearance="subtle"
                    icon={
                        (!capabilities.supportsChatRenaming || isTitleGenerating) ? (
                            <EditDisabled />
                        ) : (
                            <EditComponent />
                        )
                    }
                    onClick={onEditTitleClick}
                    disabled={!capabilities.supportsChatRenaming || isTitleGenerating}
                />
            </Tooltip>
            {capabilities.supportsDownloadChatSession && (
                <Tooltip content={'Download chat session'} relationship="label">
                    <Button
                        className={styles.actionButton}
                        size="small"
                        appearance="subtle"
                        icon={<ArrowDownload20Filled />}
                        onClick={() => void onDownloadChat(chatId)}
                    />
                </Tooltip>
            )}
            <DeleteChatDialog chatName={chatName} onDelete={() => onDeleteChat(chatId)} />
        </div>
    );
};

export default ChatListItemActions;
